import Action from "./class.Action";
import {Parsers} from "../class.TransporterParser.js";
import * as Input from "../class.TransporterInput";
import * as ActionUtils from "./class.ActionUtils";

/**
 * Creates a bubble for short-answer/free-response bubbles.
 */
export default class ShortAnswerProblem extends Action {
	constructor(options) {
		super(options);

		// Reference to the last rendered bubble.
		this.activeBubbleView = null;

		// Index of the next hint to show.
		this.nextHint = 0;
		this.lastHint = null;

		// Is this parsed?
		this.isParsed = !this.model.type.match("unparsed");

		// Number of attempts remaining before permitting Give Up button.
		this.attemptsRemaining = 1;
		// The attempt we are on.
		this.attempt = 0;
		// Whether the user gave up.
		this.gaveUp = false;

		// Track submissions so we can catch repeats.
		this.submissions = [];

		// Array of formatting tip HTML.
		this.tips = null;

		// Keep track of whether parsing calls failed.
		this.parsingError = false;

		this.initializeAnalytics();
		this.renderResponseBubble(this.$anchor);
	}

	/**
	 * Returns promise which resolves with the index of an option
	 * which matches the student's response.
	 *
	 * Resolves -1 if the answer was incorrect.
	 *
	 * @param response The student's response.
	 * @returns A promise resolving with the index of the matching option, or -1.
	 */
	parseResponse(response) {
		return new Promise(async (resolve, reject) => {
			var index = -1;
			var options = this.model.options;
			if (options.length === 0) {
				resolve(index);
			}
			try {
				index = await Parsers.auto.indexOfResponse(options, response);
				resolve(index);
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * Traverse the flow after this problem has been passed.
	 * @param {*} $problemAnchor
	 */
	passProblem($problemAnchor) {
		if (this.activeBubbleview) {
			this.activeBubbleView = null;
		}
		var trigger = "trigger";
		var hasGiveUpTrigger =
			this.model["give-up-trigger"] && this.model["give-up-trigger"].length;
		if (this.gaveUp) {
			if (this.taTrial) {
				this.taTrial.setGaveUp();
			}
			if (hasGiveUpTrigger) {
				trigger = "give-up-trigger";
			}
		} else {
			if (this.taTrial) {
				this.taTrial.setPassed();
			}
		}
		this.runnerController.createAndTraverseFlow({
			pointer: this.pointer,
			item: this.model,
			flow: [{traverse: trigger}],
			onAdvance: ($anchor) => {
				this.onAdvance($anchor);
			},
			onBypass: () => {
				this.onBypass();
			},
			$anchor: $problemAnchor,
			context: this.context,
		});
	}

	/**
	 * Renders a bubble with an input and multiple buttons
	 * so the student can respond to the problem.
	 * Mutates `this.activeBubbleView`
	 *
	 * @param {*} $problemAnchor
	 */
	async renderResponseBubble($problemAnchor) {
		if (!this.tips && this.isParsed) {
			try {
				this.tips = await this.getTips();
			} catch (e) {
				this.tips = [];
			}
		}

		if (this.taItem) {
			this.taTrial = this.taItem.createTrial({
				attempt: this.attempt,
				last_hint: this.lastHint,
			});
		}

		// Create the bubble.
		var probBubbleView = this.layoutView.placeBubble(
			$problemAnchor,
			"after",
			"student"
		);
		this.activeBubbleView = probBubbleView;
		probBubbleView.$el.addClass("short-answer");

		// Create the text input.
		const $inputDiv = $(`<div class="input-row"/>`);
		var input = new Input.TextInput({
			attributes: {
				inputController: this.inputController,
				className: "input-text",
				tagName: "input",
				target: this.pointerStr + "Attempt" + this.attempt,
			},
		});
		input.onChange({
			validator: () => {
				if (!this.model.is_numeric) {
					return true;
				} else {
					const val = input.$el.val();
					return !val.match(/[^0-9\+\-\*\/\^\=\<\>\.\,\:\(\)\[\]\{\}\%\s!\|]/);
				}
			},
			callback: () => {
				if (this.taItem) {
					this.taItem.setInteracted();
				}
				const val = input.$el.val().trim();
				if (!val.length) {
					submitButton.$el.addClass("off");
				} else {
					submitButton.$el.removeClass("off");
				}
			},
		});
		probBubbleView.appendContent($inputDiv);
		$inputDiv.append(input.$el);

		if (this.tips && this.tips.length) {
			const $formattingTips = $(
				`<span class="formatting-tips aops-font">?</span>`
			).click(() => {
				this.mainController.tipsController.setContent(this.tips);
				this.mainController.tipsController.toggleTipModal();
			});
			$inputDiv.append($formattingTips);
		}
		// Create the button div.
		var $buttonDiv = $(`<div class="btn-submit-container"/>`);
		probBubbleView.appendContent($buttonDiv);

		// Create the hint button
		if (this.model.hints.length > this.nextHint) {
			var hintPrompt = this.model.hints[this.nextHint].prompt;
			$buttonDiv.addClass("has-hint");
			var hintButton = new Input.Button({
				attributes: {
					inputController: this.inputController,
					className: "btn-like",
					tagName: "a",
					target:
						this.pointerStr + "Attempt" + this.attempt + "Hint" + this.nextHint,
				},
			});
			hintButton.$el.html(
				$(`<div class='inner-content' />`).append(
					(hintPrompt && hintPrompt.html) || "I need a hint!"
				)
			);
			hintButton.$el.addClass("color1");

			// Bind the hint button's action.
			hintButton.onClick({
				once: true,
				callback: async () => {
					if (this.taItem) {
						this.taItem.setInteracted();
					}
					if (this.taTrial) {
						this.taTrial.setRequestedHint();
						this.lastHint = this.nextHint;
					}
					this.attempt++;
					probBubbleView.setContent(
						(hintPrompt && hintPrompt.html) || "I need a hint!"
					);
					probBubbleView.$el.removeClass("short-answer");
					await this.animationController.FadeIn.start({
						bubbleView: probBubbleView,
					});

					// Run any actions in the "trigger" section for this button.
					this.runnerController.createAndTraverseFlow({
						pointer: this.pointer.concat(["hints", this.nextHint]),
						item: this.model.hints[this.nextHint],
						flow: [{traverse: "trigger"}],
						onAdvance: ($anchor) => {
							// Re-render the response bubble.
							this.nextHint++;
							this.renderResponseBubble($anchor);
						},
						onBypass: () => {
							this.onBypass();
						},
						$anchor: probBubbleView.$el,
						context: this.context,
					});
				},
			});
			$buttonDiv.append(hintButton.$el);
		} else if (this.attemptsRemaining <= 0) {
			// Create the Give Up button.
			$buttonDiv.addClass("has-hint");
			var surrenderButton = new Input.Button({
				attributes: {
					inputController: this.inputController,
					className: "btn-like",
					tagName: "a",
					target: this.pointerStr + "Attempt" + this.attempt + "Surrender",
				},
			});
			surrenderButton.$el.html("Give Up");
			surrenderButton.$el.addClass("color3");

			// Bind the give up button's action.
			surrenderButton.onClick({
				once: true,
				callback: async () => {
					if (this.taItem) {
						this.taItem.setInteracted();
					}
					if (this.taTrial) {
						this.taTrial.setGaveUp();
					}
					this.gaveUp = true;
					probBubbleView.setContent("I give up.");
					probBubbleView.$el.removeClass("short-answer");
					await this.animationController.FadeIn.start({
						bubbleView: probBubbleView,
					});
					this.passProblem(probBubbleView.$el);
				},
			});
			$buttonDiv.append(surrenderButton.$el);
		}

		// Create the submit button
		var submitButton = new Input.Button({
			attributes: {
				inputController: this.inputController,
				className: "btn-like",
				tagName: "a",
				target: this.pointerStr + "Attempt" + this.attempt + "Submit",
			},
		});
		submitButton.$el.html("Submit");
		submitButton.$el.addClass("color2");

		submitButton.onClick({
			once: true,
			callback: async () => {
				if (this.taItem) {
					this.taItem.setInteracted();
				}
				this.attempt++;
				const response = input.$el.val();
				let key, option;
				if (this.taTrial) {
					this.taTrial.setResponse(response ? JSON.stringify(response) : null);
				}

				if (response.length > 0) {
					probBubbleView.setText(response);
				} else {
					probBubbleView.setContent($("<em>No response.</em>"));
				}
				probBubbleView.$el.removeClass("short-answer");

				this.attemptsRemaining = Math.max(0, this.attemptsRemaining - 1);

				if (!this.isParsed) {
					probBubbleView.pass();
					return this.passProblem(probBubbleView.$el);
				}

				// Parse the response, assume incorrect if there's a
				// failure, such as bad/failed AJAX.
				try {
					key = await this.parseResponse(response);
					option = this.model.options[key];
					this.parsingError = false;
				} catch (e) {
					key = -1;
					option = undefined;
					this.parsingError = true;
					this.showableErrorMessage = this.determineErrorType(e);
				}

				const response_fmt = response.toLowerCase().trim();
				const alreadySubmitted = this.submissions.indexOf(response_fmt) >= 0;
				this.submissions.push(response_fmt);

				if (alreadySubmitted && !(option && option.is_correct)) {
					if (this.taTrial) {
						this.taTrial.setFailed();
					}
					probBubbleView.fail();
					await this.renderTeacherResponse(this.activeBubbleView.$el, true);
					if (ActionUtils.getRerenderPrompts()) {
						this.rerenderPrompt(this.activeBubbleView.$el);
					} else {
						this.renderResponseBubble(this.activeBubbleView.$el);
					}
				} else if (option && option.trigger) {
					if (option.is_correct) {
						probBubbleView.pass();
					} else {
						if (this.taTrial) {
							this.taTrial.setFailed();
						}
						probBubbleView.fail();
					}
					// Traverse the new flow for this response.
					this.runnerController.createAndTraverseFlow({
						pointer: this.pointer.concat(["options", key]),
						context: this.context,
						item: option,
						flow: [{traverse: "trigger"}],
						onAdvance: ($anchor) => {
							if (option.is_correct) {
								this.passProblem($anchor);
							} else {
								if (this.taTrial) {
									this.taTrial.setFailed();
								}
								if (ActionUtils.getRerenderPrompts()) {
									this.rerenderPrompt($anchor);
								} else {
									this.renderResponseBubble($anchor);
								}
							}
						},
						onBypass: () => {
							this.onBypass();
						},
						$anchor: probBubbleView.$el,
					});
				} else if (option && option.is_correct) {
					// A correct option with no trigger.
					probBubbleView.pass();
					var teacherBubbleView = this.layoutView.placeBubble(
						this.activeBubbleView.$el,
						"after",
						"teacher"
					);
					this.activeBubbleView = teacherBubbleView;

					teacherBubbleView.setContent(
						ActionUtils.getRandomTeacherResponse("positive")
					);

					await this.animationController.FadeIn.start({
						bubbleView: teacherBubbleView,
					}).scroll();

					this.passProblem(this.activeBubbleView.$el);
				} else if (this.model.options.length > 0) {
					// A wrong option with no trigger.
					if (this.taTrial) {
						this.taTrial.setFailed();
					}
					probBubbleView.fail();
					await this.renderTeacherResponse(this.activeBubbleView.$el);
					if (ActionUtils.getRerenderPrompts()) {
						this.rerenderPrompt(this.activeBubbleView.$el);
					} else {
						this.renderResponseBubble(this.activeBubbleView.$el);
					}
				} else {
					// There are no right/wrong options! So just pass it.
					probBubbleView.pass();
					this.passProblem(this.activeBubbleView.$el);
				}
			},
		});
		submitButton.$el.addClass("off");
		$buttonDiv.append(submitButton.$el);

		// Bind Enter key to Submit click.
		input.$el.keypress("enter", (event) => {
			if (event.keyCode === 13) {
				if (!submitButton.$el.hasClass("off")) {
					input.$el.blur();
					submitButton.$el.click();
				}
			}
		});

		await this.animationController.FadeIn.start({
			bubbleView: probBubbleView,
		}).scroll();

		input.$el.focus();
		this.inputController.request({enableAnimations: true});
	}

	/**
	 * Render's a default response from the teacher's side.
	 * Mutates `this.activeBubbleView`.
	 *
	 * @param {*} $anchor
	 */
	async renderTeacherResponse($anchor, alreadySubmitted = false) {
		var teacherBubbleView = this.layoutView.placeBubble(
			$anchor,
			"after",
			"teacher"
		);
		this.activeBubbleView = teacherBubbleView;

		if (this.parsingError) {
			var defaultErrorMessage =
				"Whoops, something went wrong. Please make sure you're connected to the internet and try submitting that again.";
			var errorMessage = this.showableErrorMessage
				? this.showableErrorMessage
				: defaultErrorMessage;

			teacherBubbleView.setContent(errorMessage);
		} else {
			teacherBubbleView.setContent(
				ActionUtils.getRandomTeacherResponse(
					alreadySubmitted ? "repeat" : "negative"
				)
			);
		}

		await this.animationController.FadeIn.start({
			bubbleView: teacherBubbleView,
		}).scroll();
	}

	/**
	 * Rerenders the body of this problem.
	 *
	 * @param {*} $anchor
	 */
	rerenderPrompt($anchor) {
		this.runnerController.createAndTraverseFlow({
			pointer: this.pointer,
			item: this.model,
			flow: [{traverse: "body"}],
			onAdvance: ($newAnchor) => {
				// Re-render the problem.
				this.renderResponseBubble($newAnchor);
			},
			onBypass: () => {
				this.onBypass();
			},
			$anchor: $anchor,
			context: this.context,
		});
	}

	async getTips() {
		const tipsController = this.mainController.tipsController;
		const options = this.model.options;
		const tips = await tipsController.computeMatchingTips(options);
		const html = await tipsController.getManyTipHTML(tips);
		return html;
	}

	/**
	 * Use this function to allowlist certain error types that
	 * can be shown to the user.
	 *
	 * @param {object} error
	 */
	determineErrorType(error) {
		var errorAllowlist = ["E_PARSER_ERROR"];

		return errorAllowlist.includes(error.name) ? error.message : null;
	}
}
